import { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from '@mui/icons-material/Description';
import moment from "moment";

import { ARL } from "../../constants";
import { getContractField, getPeriodTaxValues } from "../../functions";
import ContractViewActions from "../contract/ContractViewActions";

const MONTHS = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

// Años que ofrece el selector de pago: desde el primero con contratos en la
// plataforma hasta el siguiente al actual (prórrogas que ya corren en él).
const FIRST_PAYMENT_YEAR = 2024;
const PAYMENT_YEARS = Array.from(
  { length: new Date().getFullYear() + 2 - FIRST_PAYMENT_YEAR },
  (_, i) => FIRST_PAYMENT_YEAR + i
);

// Índice del plan (meses desde el mes de inicio) que corresponde al mes de
// pago elegido. Se mide con año: restar solo el número de mes dejaba fuera
// los periodos de un contrato que cruza de año (enero − noviembre < 0).
const getPaymentIndex = (contract, selectedYear, selectedMonth) => {
  if (!contract.startedAt || !selectedYear || !selectedMonth) return null;
  const paymentMonth = moment([selectedYear, selectedMonth - 1, 1]);
  const paymentIndex = paymentMonth.diff(moment(contract.startedAt).startOf("month"), "months");
  return paymentIndex >= 0 ? paymentIndex : null;
};

const getPaymentKeyForMonth = (contract, selectedYear, selectedMonth) => {
  if (!contract.payments) return null;
  const paymentIndex = getPaymentIndex(contract, selectedYear, selectedMonth);
  if (paymentIndex == null) return null;
  const key = `payment_${paymentIndex}`;
  return contract.payments[key] != null ? key : null;
};

const getPaymentStatus = (contract, selectedYear, selectedMonth) => {
  const paymentKey = getPaymentKeyForMonth(contract, selectedYear, selectedMonth);
  if (!paymentKey) return null;
  const payment = contract.payments[paymentKey];
  const hasActivities = payment.activities?.length > 0;
  if (payment.approved) return "approved"; // Aprobado (con o sin actividades)
  if (hasActivities) return "pending"; // Pendiente de aprobación
  return null; // No hay periodo de pago
};

const ChargeAccountList = ({
  contracts,
  setShowTemplate
}) => {
  const user = useSelector((state) => state.user.value);
  const users = useSelector((state) => state.user.users);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const downloadChargeAccount = (url) => {
    const separator = url.includes('?') ? '&' : '?';
    const a = document.createElement('a');
    a.href = `${url}${separator}v=${Date.now()}`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDownloadCSV = () => {
    const monthLabel = MONTHS.find((m) => m.value === selectedMonth)?.label || "";
    const headers = [
      "Naturaleza del contrato",
      "Número de contrato",
      "Correo",
      "Identificación",
      "Nombre Empleado",
      "Apellido Empleado",
      "Fecha Inicial",
      "Fecha de cierre",
      `Acta de ${monthLabel} ${selectedYear}`,
      "Valor del contrato",
      "Valor cuenta de cobro",
      "Salud",
      "Pensión",
      "ARL",
      "D59 (IU Env)",
      "D63 (Pro Hosp)",
      "D87 (Pro Bien)",
      "D88 (Pro Dllo)",
      "D89 (Politécnico)",
      "D124 (IU Digital)",
      "Total descuentos",
      "Total cuenta de cobro",
    ];

    const escapeCSV = (value) => {
      const str = String(value ?? "");
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    // When filter is "all", CSV only exports approved + pending (excludes contracts with no payment period)
    const csvContracts = statusFilter === "all"
      ? filteredContracts.filter((c) => getPaymentStatus(c, selectedYear, selectedMonth) != null)
      : filteredContracts;

    const rows = csvContracts.map((contract) => {
      // El plan almacenado es el pactado al firmar: las novedades (suspensión,
      // terminación, adición y prórroga) solo se reflejan al resolverlo, igual
      // que en la cuenta de cobro.
      const getContractFieldWithNews = getContractField(contract.news || [], contract);
      const paymentPlan = getContractFieldWithNews("paymentPlan");
      const finishedAt = getContractFieldWithNews("finishedAt");

      const paymentIndex = getPaymentIndex(contract, selectedYear, selectedMonth);
      const actaValue = paymentIndex != null && paymentPlan?.[paymentIndex] != null
        ? Math.round(paymentPlan[paymentIndex])
        : "";

      // Deduction calculations
      const pay = paymentIndex != null ? paymentPlan?.[paymentIndex] ?? 0 : 0;
      const { salarioMinimo, uvt } = getPeriodTaxValues(contract.startedAt, paymentIndex ?? 0);
      const salary = (pay * 0.4) > salarioMinimo ? (pay * 0.4) : salarioMinimo;
      const arlRate = parseFloat(ARL[contract.arlLevel]?.value || 0);

      const salud = pay ? Math.round(salary * 0.125) : "";
      const pension = pay ? Math.round(salary * 0.16) : "";
      const arl = pay ? Math.round(salary * arlRate) : "";
      const d59 = pay ? Math.round(pay * 0.004) : "";
      const d63 = pay ? (pay > 145 * uvt ? Math.round(pay * 0.01) : 0) : "";
      const d87 = pay ? Math.round(pay * 0.02) : "";
      const d88 = pay ? Math.round(pay * 0.006) : "";
      const d89 = pay ? Math.round(pay * 0.004) : "";
      const d124 = pay ? (pay > 300 * uvt ? Math.round(pay * 0.004) : 0) : "";

      const totalDescuentos = pay ? (salud + pension + arl + d59 + d63 + d87 + d88 + d89 + d124) : "";
      // Los valores del plan traen decimales (la tarifa diaria de un mes con
      // novedades, o un valor pactado no entero): el CSV los lleva al peso,
      // como la cuenta de cobro. Los descuentos se calculan sobre el valor sin
      // redondear, igual que antes, y ya salen redondeados uno a uno.
      const contractBudget = getContractFieldWithNews("contractBudget");
      const totalCuentaCobro = pay ? Math.round(pay - totalDescuentos) : "";

      return [
        contract.nature,
        contract.contractCode,
        contract.contractor?.email,
        contract.contractor?.nuip,
        contract.contractor?.name,
        contract.contractor?.lastName,
        contract.startedAt ? contract.startedAt.format("DDMMYYYY") : "",
        finishedAt ? finishedAt.format("DDMMYYYY") : "",
        actaValue,
        contractBudget != null ? Math.round(contractBudget) : "",
        pay ? Math.round(pay) : "",
        salud,
        pension,
        arl,
        d59,
        d63,
        d87,
        d88,
        d89,
        d124,
        totalDescuentos,
        totalCuentaCobro,
      ].map(escapeCSV).join(",");
    });

    const csvContent = [headers.map(escapeCSV).join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `cuentas_cobro_${monthLabel.toLowerCase()}_${selectedYear}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filteredContracts = contracts.filter((contract) => {
    // Text search filter
    if (searchText.trim()) {
      const term = searchText.toLowerCase();
      const name = contract.contractor?.name || "";
      const email = contract.contractor?.email || "";
      const nuip = contract.contractor?.nuip || "";
      const code = contract.contractCode || "";
      if (
        !name.toLowerCase().includes(term) &&
        !email.toLowerCase().includes(term) &&
        !nuip.toLowerCase().includes(term) &&
        !code.toLowerCase().includes(term)
      ) return false;
    }

    // Status filter
    if (statusFilter !== "all") {
      const status = getPaymentStatus(contract, selectedYear, selectedMonth);
      if (statusFilter === "approved" && status !== "approved") return false;
      if (statusFilter === "pending" && status !== "pending") return false;
    }

    return true;
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Buscar contratista..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "action.active", mr: 1 }} />,
            }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Año de pago</InputLabel>
            <Select
              value={selectedYear}
              label="Año de pago"
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {PAYMENT_YEARS.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Mes de pago</InputLabel>
            <Select
              value={selectedMonth}
              label="Mes de pago"
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {MONTHS.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={statusFilter}
              label="Estado"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="approved">Aprobados</MenuItem>
              <MenuItem value="pending">Pendiente de aprobación</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCSV}
          >
            CSV
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={async () => {
              const monthLabel = `${MONTHS.find((m) => m.value === selectedMonth)?.label?.toLowerCase() || ""}_${selectedYear}`;
              const urls = filteredContracts
                .map((contract) => {
                  const paymentKey = getPaymentKeyForMonth(contract, selectedYear, selectedMonth);
                  const payment = paymentKey ? contract.payments[paymentKey] : null;
                  // Consolidado (acta + firma + seguridad social) si existe; si no, el acta sola.
                  const url = payment?.chargeAccountFull || payment?.chargeAccount || null;
                  if (!url) return null;
                  return {
                    url,
                    name: `${monthLabel}_${contract.contractCode}_${paymentKey}`,
                  };
                })
                .filter(Boolean);

              if (urls.length === 0) return;

              try {
                const response = await fetch(
                  `https://handlepdfdownload-${process.env.REACT_APP_RUN_ID}.us-east1.run.app`,
                  {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      Authorization: `Bearer ${user.token}`,
                    },
                    body: JSON.stringify({ urls, zipName: `cuentas_cobro_${monthLabel}` }),
                  }
                );

                if (!response.ok) throw new Error("Error al descargar ZIP");

                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `cuentas_cobro_${monthLabel}.zip`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
              } catch (error) {
                console.error("Error al descargar PDFs:", error);
              }
            }}
          >
            Descargar PDFs
          </Button>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Código</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Contratista</TableCell>
                <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>Pago</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    No se encontraron contratos
                  </TableCell>
                </TableRow>
              ) : (
                [...filteredContracts].sort((a, b) => (a.contractCode || "").localeCompare(b.contractCode || "")).map((contract, index) => {
                  const paymentKey = getPaymentKeyForMonth(contract, selectedYear, selectedMonth);
                  return (
                    <TableRow
                      key={contract.contractId || index}
                      sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}
                    >
                      <TableCell>
                        <Typography variant="body2">
                          {contract.contractCode || "--"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {contract.contractor
                            ? `${contract.contractor.name || ""} ${contract.contractor.lastName || ""}`.trim()
                            : "Sin asignar"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {paymentKey ? (<>
                          <Tooltip title="Previsualizar Cuenta de cobro">
                            <IconButton
                              size="small"
                              color={contract.payments[paymentKey].approved ? "success" : "info"}
                              onClick={() => setShowTemplate({ contract, payment: paymentKey })}
                            >
                              <DescriptionIcon />
                            </IconButton>
                          </Tooltip>
                          {contract.payments[paymentKey].approved && (
                            <ContractViewActions
                              contract={contract}
                              payment={contract.payments[paymentKey]}
                              paymentKey={paymentKey}
                              idx={parseInt(paymentKey.split('_')[1])}
                              user={user}
                              users={users}
                              getContractFieldWithNews={getContractField(contract.news || [], contract)}
                              downloadChargeAccount={downloadChargeAccount}
                            />
                          )}
                        </>) : (
                          <Typography variant="body2" color="text.secondary">--</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
  );
};

export default ChargeAccountList;
